import { session, uuid } from './auth';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string) || 'https://api.ifa256.com/odu';
const APP_ID   = (import.meta.env.VITE_APP_ID as string) || 'ifa-app-admin';

export class ApiError extends Error {
  status: number;
  code?: string;
  data?: unknown;
  constructor(message: string, status: number, code?: string, data?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

interface RequestOpts {
  body?: unknown;
  idempotent?: boolean;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export async function request<T>(method: string, path: string, opts: RequestOpts = {}): Promise<T> {
  const url = path.startsWith('http') ? path : BASE_URL + path;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'App-Id': APP_ID,
    'Client-Id': session.clientId,
    'Device-Id': session.deviceId,
    'App-Version': '1.0.0',
    'platform': 'web',
    ...(opts.headers || {}),
  };
  if (session.token) headers['Token'] = session.token;
  if (opts.idempotent && !headers['Idempotency-Key']) headers['Idempotency-Key'] = uuid();

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError('Network error', 0);
  }

  if (res.status === 204) return undefined as T;

  let data: any = null;
  const ct = res.headers.get('content-type') || '';
  try {
    data = ct.includes('application/json') ? await res.json() : await res.text();
  } catch {}

  if (res.status === 401) {
    session.clear();
    throw new ApiError('Session expired', 401, 'UNAUTHORIZED');
  }

  if (!res.ok) {
    const msg = (data && typeof data === 'object' && (data.message || data.error)) ||
                (typeof data === 'string' && data) ||
                `Request failed (${res.status})`;
    throw new ApiError(msg, res.status, data?.code, data);
  }

  return data as T;
}

export const api = {
  get:  <T>(p: string, o?: RequestOpts) => request<T>('GET', p, o),
  post: <T>(p: string, body?: unknown, o?: RequestOpts) => request<T>('POST', p, { ...o, body }),
  put:  <T>(p: string, body?: unknown, o?: RequestOpts) => request<T>('PUT', p, { ...o, body }),
  del:  <T>(p: string, o?: RequestOpts) => request<T>('DELETE', p, o),
};

/** Direct upload to S3 signed URL — bypasses our headers. */
export async function putToSignedUrl(url: string, blob: Blob, contentType: string, signal?: AbortSignal): Promise<string> {
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: blob,
    signal,
  });
  if (!res.ok) throw new ApiError(`S3 PUT failed (${res.status})`, res.status);
  const etag = res.headers.get('etag') || res.headers.get('ETag') || '';
  return etag.replace(/"/g, '');
}

export { BASE_URL, APP_ID };
